'use client';

/**
 * ButtonModeContent
 *
 * Renders the purchase flow in "button" display mode.
 * Shows either a native payment button (e.g., Apple Pay web component)
 * or a styled wrapper around children content. Includes an optional
 * status popup (Popover) for showing processing state.
 */

import React from 'react';
import { Popover } from '@barges/react';
import cn from 'classnames';
import type { PurchaseMetadata } from '../types/processing';
import type { UsePurchaseFlowCoreReturn } from '../hooks/usePurchaseFlowCore';
import { getKindConfig } from '../config/gatewayRegistry';
import { FlowHeader } from './FlowHeader';
import PaymentProcessingScreen from '../screens/PaymentProcessingScreen';
import classes from '../screens/classes.module.scss';
import containerClasses from '../containers/classes.module.scss';

// ============================================================================
// Types
// ============================================================================

interface ButtonModeContentProps {
  core: UsePurchaseFlowCoreReturn;
  metadata?: PurchaseMetadata;
  disabled: boolean;
  buttonClassName?: string;
  showStatusPopup: boolean;
  successButtonTextKey?: string;
  processorElement: React.ReactNode;
  onFlowStarted?: () => void;
  children: React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

const ButtonModeContent: React.FC<ButtonModeContentProps> = ({
  core,
  disabled,
  buttonClassName,
  showStatusPopup,
  successButtonTextKey,
  processorElement,
  onFlowStarted,
  children
}) => {
  const {
    dataLoaded,
    buttonProcessing,
    supportCheckLoading,
    isSelectedMethodSupported,
    chargeProcessing,
    renderTopOverlay,
    statementNote,
    handlePayClick,
    setButtonProcessing,
    handleBackClick,
    handleCloseClick,
    screenCallbacks,
    handleRetryClick,
    selectedPaymentMethod,
    nativeButtonReady
  } = core;

  const handleButtonPayClick = () => {
    onFlowStarted?.();
    setButtonProcessing(true);
    handlePayClick();
  };

  // Google Pay (Checkout.com Flow) prepares its button up front; keep the button
  // in its loading state until ready so the user's first real click opens the
  // sheet within the gesture window (MBW-2436).
  const nativeNotReady = !!selectedPaymentMethod
    && !!getKindConfig(selectedPaymentMethod.kind)?.rendersOwnPayButton
    && !nativeButtonReady;

  const isLoading = !dataLoaded || supportCheckLoading || nativeNotReady;
  const isButtonDisabled = disabled || buttonProcessing || isLoading || !isSelectedMethodSupported;

  // --- Loading skeleton ---
  if (isLoading) {
    return (
      <>
        <div className={ cn(buttonClassName, containerClasses.buttonSkeleton) } data-testid="purchase-button-loading-skeleton">{children}</div>
        {processorElement}
      </>
    );
  }

  // --- Standard button wrapper ---
  return (
    <>
      <div
        className={ cn(buttonClassName, { [classes.payButtonDisabled]: isButtonDisabled }) }
        onClick={ () => { if (!isButtonDisabled) handleButtonPayClick(); } }
        onKeyDown={ (e) => { if (e.key === 'Enter' && !isButtonDisabled) handleButtonPayClick(); } }
        style={ isButtonDisabled ? { pointerEvents: 'none' } : undefined }
        role="button"
        tabIndex={ isButtonDisabled ? -1 : 0 }
        data-testid="purchase-button-wrapper"
      >
        {children}
      </div>
      {processorElement}
      {showStatusPopup && (
        <Popover
          className={ classes.popover }
          isOpen={ buttonProcessing || chargeProcessing.state.status !== 'idle' }
          centered
          style={ { rowGap: 0 } }
          contentClassName={ classes.popoverContent }
          overlayClassName={ classes.errorOverlayContainer }
          renderTopOverlay={ renderTopOverlay }
        >
          <div className={ classes.flowContent }>
            <FlowHeader titleKey="" showBackButton={ false } showCloseButton={ false } onBack={ handleBackClick } onClose={ handleCloseClick } />
            <PaymentProcessingScreen
              chargeState={ chargeProcessing.state }
              statementNote={ statementNote }
              successButtonTextKey={ successButtonTextKey }
              onOkClick={ screenCallbacks.handleOkClick }
              onRetryClick={ handleRetryClick }
              onCloseClick={ screenCallbacks.handleCloseClick }
              onSpendingLimitsClick={ screenCallbacks.handleSpendingLimitsClick }
            />
          </div>
        </Popover>
      )}
    </>
  );
};

export default ButtonModeContent;
